import { supabase } from '../lib/supabase';

export const earningsService = {
  // Get earnings data for current contractor with filters
  async getContractorEarnings(filters = {}) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    let query = supabase?.from('timesheets')?.select(`
        *,
        job_instances!inner(
          *,
          jobs!inner(
            job_name,
            job_type,
            location_address,
            payment_type,
            payment_amount,
            client_id,
            job_site_id,
            client:clients(id, client_name),
            job_site:job_sites(id, site_name, address),
            companies!inner(company_name, company_tag)
          )
        )
      `)?.eq('contractor_id', user?.id)?.order('clock_in_time', { ascending: false });

    // Apply filters
    if (filters?.startDate) {
      query = query?.gte('clock_in_time', filters?.startDate);
    }
    if (filters?.endDate) {
      query = query?.lte('clock_out_time', filters?.endDate);
    }
    if (filters?.status && filters?.status !== 'all') {
      query = query?.eq('status', filters?.status);
    }
    if (filters?.companyId) {
      query = query?.eq('company_id', filters?.companyId);
    }
    if (filters?.paymentType && filters?.paymentType !== 'all') {
      query = query?.eq('job_instances.jobs.payment_type', filters?.paymentType);
    }

    const { data, error } = await query;
    if (error) throw error;

    // Convert to camelCase for React
    return data?.map(row => ({
      id: row?.id,
      status: row?.status,
      totalHours: parseFloat(row?.total_hours || 0),
      amountEarned: parseFloat(row?.amount_earned || 0),
      clockInTime: row?.clock_in_time,
      clockOutTime: row?.clock_out_time,
      approvedAt: row?.approved_at,
      approvedBy: row?.approved_by,
      companyId: row?.company_id,
      jobInstanceId: row?.job_instance_id,
      createdAt: row?.created_at,
      jobName: row?.job_instances?.jobs?.job_name || 'Unknown Job',
      jobType: row?.job_instances?.jobs?.job_type || 'one_off',
      // Location: prefer job_site address, fall back to location_address
      locationAddress: row?.job_instances?.jobs?.job_site?.address || row?.job_instances?.jobs?.location_address || '',
      // New: Client / Job Site
      clientId: row?.job_instances?.jobs?.client_id,
      client: row?.job_instances?.jobs?.client,
      jobSiteId: row?.job_instances?.jobs?.job_site_id,
      jobSite: row?.job_instances?.jobs?.job_site,
      paymentType: row?.job_instances?.jobs?.payment_type || 'hourly',
      paymentAmount: parseFloat(row?.job_instances?.jobs?.payment_amount || 0),
      companyName: row?.job_instances?.jobs?.companies?.company_name || 'Unknown Company',
      companyTag: row?.job_instances?.jobs?.companies?.company_tag || '',
      scheduledDate: row?.job_instances?.scheduled_date
    }));
  },

  // Get earnings summary stats
  async getEarningsSummary() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('timesheets')?.select('amount_earned, total_hours, clock_in_time, status')?.eq('contractor_id', user?.id);

    if (error) throw error;

    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekStart = new Date(now.setDate(now.getDate() - now.getDay()));
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const yearStart = new Date(now.getFullYear(), 0, 1);

    const stats = {
      totalEarnings: 0,
      totalJobs: 0,
      approvedEarnings: 0,
      pendingEarnings: 0,
      todayEarnings: 0,
      weeklyEarnings: 0,
      monthlyEarnings: 0,
      yearlyEarnings: 0,
      totalHours: 0
    };

    data?.forEach(row => {
      const amount = parseFloat(row?.amount_earned || 0);
      const hours = parseFloat(row?.total_hours || 0);
      const clockInTime = new Date(row.clock_in_time);

      stats.totalEarnings += amount;
      stats.totalJobs += 1;
      stats.totalHours += hours;

      if (row?.status === 'approved') {
        stats.approvedEarnings += amount;
      } else if (row?.status === 'pending') {
        stats.pendingEarnings += amount;
      }

      if (clockInTime >= todayStart) {
        stats.todayEarnings += amount;
      }
      if (clockInTime >= weekStart) {
        stats.weeklyEarnings += amount;
      }
      if (clockInTime >= monthStart) {
        stats.monthlyEarnings += amount;
      }
      if (clockInTime >= yearStart) {
        stats.yearlyEarnings += amount;
      }
    });

    return stats;
  },

  // Get companies contractor works for
  async getContractorCompanies() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('timesheets')?.select(`
        company_id,
        job_instances!inner(
          jobs!inner(
            companies!inner(company_name, company_tag)
          )
        )
      `)?.eq('contractor_id', user?.id);

    if (error) throw error;

    // Get unique companies
    const companiesMap = new Map();
    data?.forEach(row => {
      const company = row?.job_instances?.jobs?.companies;
      if (company && !companiesMap?.has(row?.company_id)) {
        companiesMap?.set(row?.company_id, {
          id: row?.company_id,
          name: company?.company_name,
          tag: company?.company_tag
        });
      }
    });

    return Array.from(companiesMap?.values());
  },

  // Get earnings chart data
  async getEarningsChartData(period = 'week') {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const now = new Date();
    let startDate;

    switch (period) {
      case 'week':
        startDate = new Date(now.setDate(now.getDate() - 7));
        break;
      case 'month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'year':
        startDate = new Date(now.getFullYear(), 0, 1);
        break;
      default:
        startDate = new Date(now.setDate(now.getDate() - 30));
    }

    const { data, error } = await supabase?.from('timesheets')?.select('amount_earned, clock_in_time, status')?.eq('contractor_id', user?.id)?.gte('clock_in_time', startDate?.toISOString())?.order('clock_in_time', { ascending: true });

    if (error) throw error;

    return data?.map(row => ({
      date: new Date(row.clock_in_time)?.toLocaleDateString(),
      earnings: parseFloat(row?.amount_earned || 0),
      status: row?.status
    }));
  }
};